/** Browser mirror of supabase/functions/_shared/salePricingOverlay.ts — display only. */

export const GUARANTEED_LADDER_CUTOVER_ISO = '2026-09-26T00:00:00+03:00';

const LIVE_CORRIDOR_SALE_VERSION = {
  startInclusiveIso: '2026-09-15T00:00:00+03:00',
  endExclusiveIso: '2026-11-15T00:00:00+03:00',
  guaranteedDeparturePricing: 'flatCap',
  sharedLadders: {
    NairobiToArusha: { 1: 239.99, 2: 129.99, 3: 94.99, 4: 76.99, 5: 63.99, 6: 54.99 },
    ArushaToNairobi: { 1: 239.99, 2: 129.99, 3: 94.99, 4: 76.99, 5: 63.99, 6: 54.99 },
    NairobiToMoshi: { 1: 309.99, 2: 177.49, 3: 131.99, 4: 106.49, 5: 89.99, 6: 77.49 },
    MoshiToNairobi: { 1: 309.99, 2: 177.49, 3: 131.99, 4: 106.49, 5: 89.99, 6: 77.49 },
  },
  clientPriceUsd: {
    arusha: { 6: 269.99, 14: 305, 22: 325 },
    moshi: { 6: 354.99, 14: 384.99, 22: 409.99 },
    jro: { 6: 329.99, 14: 359.99, 22: 384.99 },
  },
  committedAloneCapUsd: {
    NairobiToArusha: 269.99,
    ArushaToNairobi: 269.99,
    NairobiToMoshi: 354.99,
    MoshiToNairobi: 354.99,
    ArushaToMoshi: 150,
    MoshiToArusha: 150,
  },
};

const LIVE_CORRIDOR_SALE_VERSION_V2 = {
  ...LIVE_CORRIDOR_SALE_VERSION,
  startInclusiveIso: GUARANTEED_LADDER_CUTOVER_ISO,
  guaranteedDeparturePricing: 'partyLadder',
};

/** @type {{ enabled: boolean, versions: typeof LIVE_CORRIDOR_SALE_VERSION[] }} */
export const SALE_PRICING_OVERLAY = {
  enabled: true,
  versions: [LIVE_CORRIDOR_SALE_VERSION, LIVE_CORRIDOR_SALE_VERSION_V2],
};

let injectedClockMs = null;

export function setSalePricingClock(isoOrDate) {
  if (isoOrDate == null) {
    injectedClockMs = null;
    return;
  }
  injectedClockMs = typeof isoOrDate === 'string' ? Date.parse(isoOrDate) : isoOrDate.getTime();
}

function resolveNow(at) {
  if (at != null) return at.getTime();
  if (injectedClockMs != null) return injectedClockMs;
  return Date.now();
}

export function resolveSaleVersion(at) {
  if (at == null && !SALE_PRICING_OVERLAY.enabled) return null;
  const now = resolveNow(at);
  let match = null;
  for (const version of SALE_PRICING_OVERLAY.versions) {
    const start = Date.parse(version.startInclusiveIso);
    const end = Date.parse(version.endExclusiveIso);
    if (now >= start && now < end) match = version;
  }
  return match;
}

export function isSalePricingActive(at) {
  return resolveSaleVersion(at) != null;
}

export function appendSalePricingVersionForTests(version) {
  SALE_PRICING_OVERLAY.versions.push(version);
}

export function resetSalePricingVersionsForTests() {
  SALE_PRICING_OVERLAY.versions.length = 0;
  SALE_PRICING_OVERLAY.versions.push(LIVE_CORRIDOR_SALE_VERSION, LIVE_CORRIDOR_SALE_VERSION_V2);
}

export function getSaleGuaranteedDeparturePricing(at) {
  const version = resolveSaleVersion(at);
  if (!version) return null;
  return version.guaranteedDeparturePricing;
}

export function getSaleSharedPerPersonRate(segment, occupancyTier, at) {
  const version = resolveSaleVersion(at);
  if (!version) return null;
  const ladder = version.sharedLadders[segment];
  if (!ladder) return null;
  const rate = ladder[occupancyTier];
  return rate == null ? null : rate;
}

export function getSaleCommittedAloneCap(segment, at) {
  const version = resolveSaleVersion(at);
  if (!version) return null;
  const cap = version.committedAloneCapUsd[segment];
  return cap == null ? null : cap;
}

export const SALE_ACTIVE_TEST_INSTANT = '2026-10-01T12:00:00+03:00';

export const SALE_V1_TEST_INSTANT = '2026-09-20T12:00:00+03:00';

const PRESET_NAME_TO_ROUTE = {
  'Nairobi ↔ Arusha': 'arusha',
  'Nairobi ↔ Moshi via Namanga': 'moshi',
  'Nairobi ↔ Kilimanjaro Airport': 'jro',
  'Kilimanjaro Airport ↔ Namanga': 'jro',
};

export function normalizeSaleCapacity(value) {
  const n = Number(value);
  if (n === 14 || n === 22) return n;
  return 6;
}

export function getSaleClientPrice(route, capacity, at) {
  const version = resolveSaleVersion(at);
  if (!version) return null;
  const map = version.clientPriceUsd[route];
  if (!map) return null;
  return map[capacity] ?? null;
}

export function saleRouteFromPresetName(presetName) {
  if (!presetName) return null;
  return PRESET_NAME_TO_ROUTE[presetName] ?? null;
}
